import React, { useMemo, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Grid,
    useTheme
} from "@mui/material";
import { tokens } from "../../assets/theme";
import messages from "./components/messages";
import Controls from "../../components/controls/Controls";
import AddIcon from "@mui/icons-material/Add";
import { FormattedMessage, useIntl } from "react-intl";
import dayjs from "dayjs";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { pushErrorMessage } from "../../utils/notifications";
import {
    useCalendarExceptions,
    useCreateCalendarException,
    useUpdateCalendarException,
    useDeleteCalendarException,
} from "../../hooks/useCalendarExceptions";
import CalendarExceptionsGrid from "./components/CalendarExceptionGrid";
import { useQueryClient } from "@tanstack/react-query";
import PasteImportCalendarExceptionsDialog from "./components/PasteImportCalendarExceptionsDialog";
import { useBulkUpsertCalendarExceptions } from "../../hooks/useCalendarExceptions";

function CalendarExceptionsTab() {
    const [year, setYear] = useState(dayjs().year());
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [companyKey] = useLocalStorage("companyKey", 1);
    const { formatMessage } = useIntl();
    const queryClient = useQueryClient();

    const from = dayjs(`${year}-01-01`).format("YYYY-MM-DD");
    const to = dayjs(`${year}-12-31`).format("YYYY-MM-DD");

    const { data: exceptions = [], isLoading } = useCalendarExceptions(companyKey, from, to);
    const createMut = useCreateCalendarException(companyKey, from, to);
    const updateMut = useUpdateCalendarException(companyKey, from, to);
    const deleteMut = useDeleteCalendarException(companyKey, from, to);
    const bulkUpsertMut = useBulkUpsertCalendarExceptions(companyKey, from, to);
    const [pasteOpen, setPasteOpen] = useState(false);

    const workspaceBg =
        theme.palette.mode === "dark" ? colors.primary[700] : colors.grey[100];

    const dateTimeIconSx =
        theme.palette.mode === "dark"
            ? {
                "& input::-webkit-calendar-picker-indicator": {
                    filter: "invert(1)",
                },
            }
            : {};

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null); // {id, date, name, comment, isWorkingDay}

    const openCreate = () => {
        setEditing({
            id: null,
            date: dayjs().format("YYYY-MM-DD"),
            name: "",
            comment: "",
            isWorkingDay: false,
        });
        setDialogOpen(true);
    };

    const openEdit = (row) => {
        setEditing({ ...row });
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setEditing(null);
    };

    const save = async () => {
        if (!editing?.date) {
            pushErrorMessage(formatMessage(messages.dateMissing));
            return;
        }

        const payload = {
            date: editing.date, // DateOnly som "YYYY-MM-DD"
            name: editing.name || null,
            comment: editing.comment || null,
            isWorkingDay: !!editing.isWorkingDay,
        };

        try {
            if (editing.id) {
                await updateMut.mutateAsync({ id: editing.id, payload });
            } else {
                await createMut.mutateAsync(payload);
            }
            await queryClient.invalidateQueries({
                queryKey: ["calendarMatrix", companyKey],
                exact: false,
            });
            closeDialog();
        } catch (e) {
            console.error(e);
            pushErrorMessage(formatMessage(messages.couldNotSaveExceptionDay));
        }
    };

    const remove = async (id) => {
        try {
            await deleteMut.mutateAsync(id);
            await queryClient.invalidateQueries({
                queryKey: ["calendarMatrix", companyKey],
                exact: false,
            });
        } catch (e) {
            console.error(e);
            pushErrorMessage(formatMessage(messages.couldNotRemoveExceptionDay));
        }
    };

    const rows = useMemo(
        () => [...exceptions].sort((a, b) => (a.date > b.date ? 1 : -1)),
        [exceptions]
    );

    return (
        <Box
            sx={{
                p: 2,
                backgroundColor: workspaceBg,
                display: "flex",
                flexDirection: "column",
                gap: 1,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                }}
            >
                <Typography variant="h5" gutterBottom>
                    <FormattedMessage {...messages.excemptedDays} />
                </Typography>

                <Box>
                    <Controls.Button
                        text={formatMessage(messages.importExceptionDays)}
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setPasteOpen(true)}
                        sx={{
                            "&:hover": { backgroundColor: "#4a799c" },
                            backgroundColor: "#4a799c",
                            color: "#ffffff",
                            textTransform: "none",
                            fontSize: "0.9rem",
                        }}
                    />
                    <Controls.Button
                        text={formatMessage(messages.addExceptionDay)}
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={openCreate}
                        sx={{
                            "&:hover": {
                                backgroundColor: "#799c4a",
                            },
                            backgroundColor: "#799c4a",
                            color: "#ffffff",
                            textTransform: "none",
                            fontSize: "0.9rem",
                            ml: 1,
                        }}
                    />
                </Box>


            </Box>
            <Grid container spacing={2} mb={1}>
                <Grid item xs={12} md={1}>
                    <TextField
                        label={<FormattedMessage {...messages.year} />}
                        type="number"
                        size="small"
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                        sx={dateTimeIconSx}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
            </Grid>
            {rows.length === 0 && !isLoading ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    <FormattedMessage {...messages.noExcemptedDaysFoundForYear} />
                </Typography>
            ) : (
                <CalendarExceptionsGrid
                    rows={rows}
                    isLoading={isLoading}
                    openEdit={openEdit}
                    remove={remove}
                />
            )}

            <Dialog
                open={dialogOpen}
                onClose={closeDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: colors.primary[500],
                        color: "#fff",
                        border: `1px solid ${colors.primary[600]}`,
                    },
                }}
            >
                <DialogTitle>{editing?.id ? formatMessage(messages.updateDay) : formatMessage(messages.addDay)}</DialogTitle>
                <DialogContent dividers>
                    {editing && (
                        <Box display="flex" flexDirection="column" gap={2} mt={1}>
                            <TextField
                                label={formatMessage(messages.date)}
                                type="date"
                                value={editing.date}
                                onChange={(e) => setEditing((p) => ({ ...p, date: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                fullWidth
                            />

                            <TextField
                                label={formatMessage(messages.name)}
                                value={editing.name}
                                onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))}
                                size="small"
                                fullWidth
                            />

                            <TextField
                                label={formatMessage(messages.comment)}
                                value={editing.comment}
                                onChange={(e) => setEditing((p) => ({ ...p, comment: e.target.value }))}
                                size="small"
                                fullWidth
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={closeDialog}>
                        <FormattedMessage {...messages.cancel} />
                    </Button>
                    <Button
                        variant="contained"
                        onClick={save}
                        disabled={createMut.isPending || updateMut.isPending}
                    >
                        <FormattedMessage {...messages.save} />
                    </Button>
                </DialogActions>
            </Dialog>
            <PasteImportCalendarExceptionsDialog
                open={pasteOpen}
                onClose={() => setPasteOpen(false)}
                isImporting={bulkUpsertMut.isPending}
                paperSx={{
                    backgroundColor: colors.primary[500],
                    color: "#fff",
                    border: `1px solid ${colors.primary[600]}`,
                }}
                onImport={async (items) => {
                    try {
                        await bulkUpsertMut.mutateAsync(items);

                        // Om du vill vara extra tydlig: invalidera även listan manuellt (fast hooken gör det redan)
                        await queryClient.invalidateQueries({ queryKey: ["calendarExceptions", companyKey, from, to] });
                        await queryClient.invalidateQueries({ queryKey: ["calendarMatrix", companyKey], exact: false });
                    } catch (e) {
                        console.error(e);
                        pushErrorMessage("Import misslyckades.");
                    }
                }}
            />
        </Box>
    );
}

export default CalendarExceptionsTab;
